import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Observable,
  ReplaySubject,
  from as observableFrom,
  of as observableOf
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  tap
} from "rxjs/operators";

import { ResponseEnvelope } from "@lantern/app/core";
import { AuthJwtService } from "@lantern/app/core/auth";
import { RuntimeConfigService } from "@lantern/app/core/utils";

import { Mart } from "@local/app/models";

import { GlobalAuthApi } from "../api/global-auth";

@Injectable()
export class GlobalAuthService {
  onInitialized = new ReplaySubject<boolean>(1);
  isLoggedIn = new ReplaySubject<boolean>(1);

  constructor(
    private authJwt: AuthJwtService,
    private globalAuthApi: GlobalAuthApi,
    private runtimeConfig: RuntimeConfigService,
    private router: Router
  ) {
    this.init();
  }

  private init(): void {
    // when initialized, check if global jwt token exists and set isLoggedIn state
    this.onInitialized
      .pipe(
        filter((initted) => initted),
        mergeMap(() => this.authJwt.globalJwtToken),
        distinctUntilChanged(),
        map((token) => token != null)
      )
      .subscribe(this.isLoggedIn);

    // get first global jwt, if exists check is valid
    this.authJwt.globalJwtToken
      .pipe(
        first(),
        mergeMap((token) => {
          if (token == null) {
            return observableOf(undefined);
          }
          return this.globalAuthApi.verifyToken(token).pipe(
            map((result) => {
              return result.valid ? token : undefined;
            }),
            catchError(() => {
              return observableOf(undefined);
            })
          );
        })
      )
      .subscribe((token) => {
        if (token == null) {
          this.authJwt.globalJwtToken.next(undefined);
        }
        this.onInitialized.next(true);
      });
  }

  getMarts(): Observable<ResponseEnvelope<Mart>> {
    return this.authJwt.globalJwtToken.pipe(
      first(),
      mergeMap((token) => {
        if (token == null) {
          throw new Error("Can't retrieve marts, must be logged in");
        }
        return this.globalAuthApi.getMarts(token);
      })
    );
  }

  loginWithPassword(
    email: string,
    password: string,
    recaptchaResponse: string
  ): Observable<{ success: true }> {
    return this.globalAuthApi.loginWithPassword(
      email,
      password,
      recaptchaResponse
    );
  }

  loginWithMagicToken(magicToken: string): Observable<void> {
    return this.globalAuthApi.loginWithMagicToken(magicToken).pipe(
      map((res) => {
        if (res != null) {
          this.authJwt.globalJwtToken.next(res.jwt);
        } else {
          this.authJwt.globalJwtToken.next(undefined);
        }
      })
    );
  }

  loginWithGoogle(credential: string): Observable<void> {
    return this.globalAuthApi.loginWithGoogle(credential).pipe(
      map((res) => {
        if (res != null) {
          this.authJwt.globalJwtToken.next(res.jwt);
        } else {
          this.authJwt.globalJwtToken.next(undefined);
        }
      })
    );
  }

  requestResetPassword(
    email: string,
    redirect?: string
  ): Observable<{ success: boolean }> {
    return observableFrom(this.runtimeConfig.value).pipe(
      map(({ globalAuthUrl }) => {
        let returnUrl = `${globalAuthUrl}/#/reset-password?email=__email__&resetToken=__token__&expiration=__expiration__`;
        if (redirect != null) {
          returnUrl += `&redirect=${redirect}`;
        }
        return returnUrl;
      }),
      mergeMap((returnUrl) => {
        return this.globalAuthApi.requestResetPassword(email, returnUrl);
      })
    );
  }

  resetPassword(
    email: string,
    password: string,
    resetToken: string
  ): Observable<{ success: boolean }> {
    return this.globalAuthApi.resetPassword(email, password, resetToken);
  }

  logout(): Observable<void> {
    return this.authJwt.globalJwtToken.pipe(
      first(),
      mergeMap((globalToken) => {
        if (globalToken == null) {
          return observableOf(undefined);
        }
        return this.globalAuthApi.logout(globalToken).pipe(mapTo(undefined));
      }),
      catchError(() => {
        return observableOf(undefined);
      }),
      tap(() => {
        this.authJwt.globalJwtToken.next(undefined);
        this.router.navigateByUrl("/login");
      })
    );
  }
}
